import * as React from 'react';
import DatePicker from 'react-date-picker';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';

import { addDays, format, getDay, isDate, parse, sub } from 'date-fns';
import { formatterDateValues } from '../../../helpers/Utils';
import { formatterDate } from '../../../helpers/FormatterDate';
import { isEqual } from 'lodash';
import { filterIncorrectDate } from '../../../helpers/filterIncorrectDate';

export interface DateRange {
  beginDate: string;
  finishDate: string;
}

interface DateTimeProps {
  value?: string[] | DateRange[] | any;
  disabled: boolean;
  maxDate?: string;
  minDate?: string;
  isRanged?: boolean;
  isMultiple?: boolean;
  multipleProps?: {
    maxLength: number;
    duration: number;
  };
  onChangeValue: (value: string | DateRange) => void;
  tableSchema?: any;
  shouldUpdateValue?: boolean;
}

interface DateTimeState {
  value: string[] | DateRange[] | any;
}

const ChooseDatePicker = ({
  value,
  minDate,
  maxDate,
  disabled,
  onChange,
  isRanged,
  tableSchema,
}) => {
  const DatePickerComponent = isRanged ? DateRangePicker : DatePicker;

  /**
   *  несмотря на то, что в стейте уже происходит та же фильтрация,
   *  если в компонент попадает некорректная дата, которая ещё не отфильтровалась в стейте,
   *  весь компонент формы падает
   *  поэтому фильтрую доп. раз здесь
   */
  const valueFiltered = filterIncorrectDate(value);

  return (
    <DatePickerComponent
      className={`form-control ${tableSchema ? 'form-date-table' : ''}`}
      format="dd.MM.y"
      value={formatterDateValues(valueFiltered)}
      minDate={formatterDate(minDate)}
      maxDate={formatterDate(maxDate)}
      disabled={disabled}
      onChange={onChange}
      portalContainer={
        tableSchema ? document.getElementById(tableSchema.id) : undefined
      }
    />
  );
};

export default class GridCalendar extends React.Component<
  DateTimeProps,
  DateTimeState
> {
  state = {
    value: [''],
  };

  componentDidMount() {
    this.setState({
      value:
        typeof this.props.value === 'object' && this.props.value?.length
          ? this.props.value
          : [this.props.value ?? ''],
    });
  }

  componentDidUpdate(prevProps: Readonly<DateTimeProps>) {
    /**
     * Если в пропсах приходит true - сейчас отображается значение с сервера,
     * прокинутое в пропсах досюда из EditorModalTable.
     *
     * Значит, нужно синхронизировать стейт EditorModalTable
     *
     * По-хорошему, эту синхронизацию стоит сделать в методe getFieldValue
     * родительского компонента EditorModalTable, но там есть
     * очень высокий риск сломать валидацию в обратную сторону
     *
     * Чтобы не валидировались на самом деле невалидные инпуты, делаю синк значений здесь, т.к.
     * только этот компонент создаёт проблемы
     */
    if (this.props.shouldUpdateValue) {
      this.props.onChangeValue(this.props.value);
    }

    if (prevProps.value !== this.props.value) {
      this.setState({
        // Если пришел объект с диапазоном, добавляем его в массив, обработал также случай когда приходит undefined
        value:
          typeof this.props.value === 'object' && this.props.value?.length
            ? this.props.value
            : [this.props.value ?? ''],
      });

      this.filterIncorrectValues();
    }
  }

  filterIncorrectValues() {
    const { value } = this.state;
    const valueFiltered = value.map(val => filterIncorrectDate(val));

    if (!isEqual(value, valueFiltered)) this.setState({ value: valueFiltered });
  }

  onChange = (value: Date | Array<Date>, key: number) => {
    let result = this.state.value;
    if (!value) {
      if (result.length > 1) {
        result.splice(key, key);
      } else {
        result = [
          // TODO: TYPE!!!!
          // @ts-ignore
          // eslint-disable-next-line react/destructuring-assignment
          this.props?.isRanged
            ? {
                beginDate: null,
                finishDate: null,
              }
            : null,
        ];
      }
    } else {
      // TODO: TYPE!!!
      // @ts-ignore
      result[key] = Array.isArray(value)
        ? {
            beginDate: format(new Date(value?.[0]), 'dd.MM.yyyy'),
            finishDate: format(new Date(value?.[1]), 'dd.MM.yyyy'),
          }
        : format(new Date(value), 'dd.MM.yyyy');
    }

    // Если в массиве 1 диапазон, превращаем его в объект, иначе передаем масси
    // TODO: !!!
    // @ts-ignore
    this.props.onChangeValue(result.length > 1 ? result : result[0]);
  };

  onSetMultiplyCalendar = () => {
    const { value } = this.state;
    const { isMultiple, multipleProps } = this.props;
    let minDateStringForMultiple;

    if (!!isMultiple && value[value.length - 1]) {
      // TODO: TYPE!!!E
      // @ts-ignore
      if (value[value.length - 1]?.finishDate) {
        // TODO: TYPE!!!
        const duration =
          multipleProps?.duration === 0 ? 1 : multipleProps?.duration;

        const minDate = addDays(
          // @ts-ignore
          parse(value[value.length - 1]?.finishDate, 'dd.MM.yyyy', new Date()),
          duration,
        );
        minDateStringForMultiple = format(minDate, 'dd.MM.yyyy');
      }
    }

    // eslint-disable-next-line no-shadow
    this.setState(({ value }) => ({
      value: [
        ...value,
        {
          beginDate:
            typeof multipleProps?.duration === 'number' && value.length > 0
              ? minDateStringForMultiple
              : '',
          finishDate: '',
        },
      ],
    }));
  };

  render() {
    const {
      isRanged,
      multipleProps,
      isMultiple,
      minDate,
      maxDate,
      disabled,
      tableSchema,
    } = this.props;
    const { value } = this.state;

    return (
      <>
        {value?.map((item, key) => {
          // У каждого следующего календаря minDate больше значения предыдущего finishDate на duration дней
          let minDateWithDuration;
          if (value[key - 1]) {
            // TODO: TYPE!!!
            // @ts-ignore
            if (value[key - 1]?.finishDate) {
              const duration =
                multipleProps?.duration === 0 ? 1 : multipleProps?.duration;

              const addDaysToDate = addDays(
                // @ts-ignore
                parse(value[key - 1]?.finishDate, 'dd.MM.yyyy', new Date()),
                duration,
              );
              minDateWithDuration = format(addDaysToDate, 'dd.MM.yyyy');
            }
          }

          return (
            <ChooseDatePicker
              value={
                // eslint-disable-next-line no-nested-ternary
                typeof multipleProps?.duration === 'number' && key > 0
                  ? // TODO: TYPE!!!
                    // @ts-ignore
                    item.finishDate
                    ? item
                    : minDateWithDuration
                  : item
              }
              minDate={
                typeof multipleProps?.duration === 'number'
                  ? minDateWithDuration
                  : minDate
              }
              maxDate={maxDate}
              // disabled={typeof multipleProps?.duration === 'number' && key > 0 ? true : disabled}
              disabled={disabled}
              onChange={_value => {
                this.onChange(_value, key);
              }}
              isRanged={isRanged}
              key={key}
              tableSchema={tableSchema}
            />
          );
        })}

        {!!isMultiple &&
        multipleProps?.maxLength > this.state.value.length &&
        !disabled ? (
          <button
            type="button"
            className="btn btn-info react-bs-table-add-btn"
            onClick={this.onSetMultiplyCalendar}
            style={{
              width: '100%',
              borderTop: 'unset',
              borderRight: 'unset',
            }}
          >
            <span
              className="glyphicon glyphicon-plus"
              style={{ marginRight: '.5rem' }}
            />
            Добавить
          </button>
        ) : null}
      </>
    );
  }
}
